import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { Section, Eyebrow, StatusBadge } from "@/components/Bits";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import StoreButtons from "@/components/StoreButtons";
import { APP_DETAILS, logo } from "@/data/site";

const THEME = {
  purple: { hero: "bg-purple text-cream", eyebrow: "text-turquoise", num: "text-turquoise" },
  turquoise: { hero: "bg-turquoise text-charcoal", eyebrow: "text-purple", num: "text-purple" },
};

const SEO_TITLES = {
  spelling: "Spelling App for Home Education | FreeRange.Education",
  "lesson-planner": "Home Education Lesson Planner | FreeRange.Education",
};

export default function AppDetail() {
  const { slug } = useParams();
  const app = APP_DETAILS[slug];
  if (!app) return <Navigate to="/apps" replace />;
  const t = THEME[app.accent] || THEME.purple;

  return (
    <>
      <Seo title={SEO_TITLES[slug] || `${app.name} | FreeRange.Education`} description={app.intro[0]} />

      {/* Hero */}
      <section className={`px-5 pb-16 pt-32 md:px-8 md:pt-40 ${t.hero}`}>
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Link to="/apps" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold opacity-80 hover:opacity-100" data-testid="back-to-apps">
              <ArrowLeft className="h-4 w-4" /> All apps
            </Link>
          </Reveal>
          <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <Reveal>
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/95 shadow-sm">
                    <img src={logo} alt="" className="h-11 w-11 rounded-full" data-testid="app-icon" />
                  </div>
                  <StatusBadge status={app.status} className="bg-white/20" testid="app-status" />
                </div>
                <h1 className="mt-6 font-display text-4xl font-extrabold leading-[0.98] tracking-tight sm:text-5xl lg:text-6xl">
                  {app.heading}
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-6 max-w-xl space-y-4 text-lg leading-relaxed opacity-90">
                  {app.intro.map((p, i) => <p key={i}>{p}</p>)}
                </div>
                <div className="mt-8">
                  <StoreButtons appSlug={slug} showWebApp />
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <div className="group">
                <ImagePlaceholder
                  src={app.shots[0]}
                  alt={`${app.name} preview`}
                  ratio="portrait"
                  rounded="rounded-[2rem]"
                  label="APP SCREENSHOT PLACEHOLDER 1"
                  testid="app-shot-1"
                  className="border-4 border-white/80"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Planned direction */}
      <Section className="py-24 md:py-32">
        <Reveal className="mb-12">
          <Eyebrow className={t.eyebrow}>Planned direction</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl">
            What we're designing towards.
          </h2>
          <p className="mt-4 max-w-xl text-charcoal/60">
            Everything below is planned / in development. We'll only claim features once they're real.
          </p>
        </Reveal>

        <Stagger className="grid gap-5 md:grid-cols-2">
          {app.features.map((f, i) => (
            <StaggerItem key={f.title}>
              <div className="flex h-full flex-col rounded-3xl border border-charcoal/10 bg-white p-8 transition-transform duration-300 hover:-translate-y-1" data-testid={`feature-${i}`}>
                <span className={`font-display text-4xl font-extrabold ${t.num}`}>0{i + 1}</span>
                <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-charcoal">{f.title}</h3>
                <p className="mt-2 leading-relaxed text-charcoal/70">{f.body}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      {/* Screenshots */}
      <Section className="pb-24 md:pb-32">
        <Reveal className="mb-10">
          <Eyebrow className={t.eyebrow}>A look ahead</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-charcoal sm:text-4xl">
            Product visuals — coming soon.
          </h2>
        </Reveal>
        <Stagger className="grid gap-6 md:grid-cols-3">
          {app.shots.map((s, i) => (
            <StaggerItem key={i} className="group">
              <ImagePlaceholder
                src={undefined}
                alt=""
                ratio="portrait"
                rounded="rounded-3xl"
                label={`APP SCREENSHOT PLACEHOLDER ${i + 1}`}
                testid={`app-screenshot-${i + 1}`}
              />
            </StaggerItem>
          ))}
        </Stagger>
        <Reveal className="mt-10 rounded-3xl bg-sand p-8">
          <p className="text-charcoal/70">
            Download links for the App Store and Google Play will appear here once {app.name} is released. The buttons
            above are placeholders and don't link anywhere yet.
          </p>
        </Reveal>
      </Section>
    </>
  );
}
